import React, { memo, useState, useCallback, useEffect, useRef } from "react";
import PropTypes from "prop-types";

import { BaseDropdown } from "widgets/basedropdown";

import { ColumnHeader } from "../columheader";
import { RowHeader } from "../rowheader";
import { getRowHeight } from "../../utils";
import { usePrintStore } from "@/formlayout/print/print.store";

import styles from "../../datasheet.css";

function DataSheetHeaderComponent(props) {
  const {
    totalWidth,
    tableGridTemplate,
    fixedColumnWidth,
    fixedColumnGridTemplate,
    fixedColumns,
    nonFixedColumns,
    onAddColumn,
    onResize,
    onReorder,
    canAddColumn,
    canSelectAll,
    onSelectAll,
    allSelected,
    addColumnPopup = null,
    previewOption,
    handlePinColumn,
    rootContainer
  } = props;

  var printMode = usePrintStore((state) => {
    return state.printMode;
  });

  const SINGLE_ROW_HEIGHT = getRowHeight();

  const [isResizing, setIsResizing] = useState(false);
  const [resizingLeft, setResizingLeft] = useState(0);
  const resizeStartingX = useRef(null);
  const resizeStartingLeft = useRef(null);
  const resizingColumn = useRef(null);
  const leftFixedRef = useRef(null);

  const [isReordering, setIsReordering] = useState(false);
  const [reorderingLeft, setReorderingLeft] = useState(0);
  const reorderingStartingX = useRef(null);
  const reorderingStartingLeft = useRef(null);
  const reorderingHighlightLeft = useRef(null);
  const reorderingColumn = useRef(null);
  const reorderHightlightColumn = useRef(null);

  const dragStartTimer = useRef(null);

  const onHeaderMouseDown = useCallback(
    function onHeaderMouseDownFunction(e) {
      const { columnId } = e.target.dataset;
      if (e.target.dataset.columnResizer) {
        let startingLeft = 0;
        resizingColumn.current = null;
        resizeStartingLeft.current = null;
        for (let i = 0; i < fixedColumns.length; i++) {
          startingLeft += fixedColumns[i].width + 1;
          if (fixedColumns[i].id === columnId) {
            resizingColumn.current = fixedColumns[i];
            resizeStartingLeft.current = rootContainer.scrollLeft;
            break;
          }
        }
        if (!resizingColumn.current) {
          for (let i = 0; i < nonFixedColumns.length; i++) {
            startingLeft += nonFixedColumns[i].width + 1;
            if (nonFixedColumns[i].id === columnId) {
              resizingColumn.current = nonFixedColumns[i];
              resizeStartingLeft.current -= fixedColumns.length;
              break;
            }
          }
        }
        setIsResizing(true);
        resizeStartingLeft.current +=
          startingLeft + SINGLE_ROW_HEIGHT * (previewOption ? 2 : 1) - 1;
        resizeStartingX.current = e.screenX;
        setResizingLeft(resizeStartingLeft.current);
      } else if (e.target.dataset.columnHeader) {
        let startingLeft = SINGLE_ROW_HEIGHT * (previewOption ? 2 : 1);
        reorderingColumn.current = null;
        reorderingStartingLeft.current = null;
        for (let i = 0; i < fixedColumns.length; i++) {
          if (fixedColumns[i].id === columnId) {
            reorderingColumn.current = fixedColumns[i];
            reorderingStartingLeft.current += rootContainer.scrollLeft;
            break;
          } else {
            startingLeft += fixedColumns[i].width + 1;
          }
        }
        if (!reorderingColumn.current) {
          for (let i = 0; i < nonFixedColumns.length; i++) {
            if (nonFixedColumns[i].id === columnId) {
              reorderingColumn.current = nonFixedColumns[i];
              break;
            } else {
              startingLeft += nonFixedColumns[i].width + 1;
            }
          }
        }
        reorderingStartingLeft.current += startingLeft;
        reorderingStartingX.current = e.screenX;
        reorderingHighlightLeft.current =
          reorderingStartingLeft.current +
          reorderingColumn.current.width -
          fixedColumns.length;
        dragStartTimer.current = setTimeout(() => {
          setIsReordering(true);
          setReorderingLeft(reorderingStartingLeft.current);
        }, 500);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fixedColumns, nonFixedColumns]
  );

  const onWindowMouseMove = useCallback(
    function onWindowMouseMoveFunction(e) {
      if (isResizing) {
        const diff =
          (window.isRTL ? -1 : 1) * (e.screenX - resizeStartingX.current);
        if (
          resizingColumn.current.width + diff >
          ((resizingColumn.current && resizingColumn.current.minWidth) || 200)
        )
          setResizingLeft(resizeStartingLeft.current + diff - 1);
      } else if (isReordering) {
        const diff =
          (window.isRTL ? -1 : 1) * (e.screenX - reorderingStartingX.current);
        let highlighterLeft = null;
        let totalLeft = 0;
        for (let i = 0; i < fixedColumns.length; i++) {
          if (reorderingStartingLeft.current + diff < totalLeft) {
            highlighterLeft =
              totalLeft +
              SINGLE_ROW_HEIGHT * (previewOption ? 2 : 1) +
              rootContainer.scrollLeft;
            reorderHightlightColumn.current =
              i > 0 ? fixedColumns[i - 1] : null;
            break;
          }
          totalLeft += fixedColumns[i].width + 1;
        }
        if (highlighterLeft === null) {
          for (let i = 0; i < nonFixedColumns.length; i++) {
            if (reorderingStartingLeft.current + diff < totalLeft) {
              highlighterLeft =
                totalLeft + SINGLE_ROW_HEIGHT * (previewOption ? 2 : 1);
              reorderHightlightColumn.current =
                i > 0
                  ? nonFixedColumns[i - 1]
                  : fixedColumns[fixedColumns.length - 1];
              break;
            }
            totalLeft += nonFixedColumns[i].width + 1;
          }
        }
        reorderingHighlightLeft.current = highlighterLeft;
        setReorderingLeft(reorderingStartingLeft.current + diff);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isResizing, isReordering, fixedColumns, nonFixedColumns]
  );

  const onWindowMouseUp = useCallback(
    function onWindowMouseUp(e) {
      clearTimeout(dragStartTimer.current);
      dragStartTimer.current = null;
      if (isResizing) {
        const diff =
          (window.isRTL ? -1 : 1) * (e.screenX - resizeStartingX.current);
        let newWidth = resizingColumn.current.width + diff;
        if (newWidth <= (resizingColumn.current.minWidth || 200)) {
          newWidth = resizingColumn.current.minWidth || 200;
        }
        onResize(resizingColumn.current, newWidth);
        setIsResizing(false);
      } else if (isReordering) {
        setIsReordering(false);
        onReorder(reorderingColumn.current, reorderHightlightColumn.current);
      }
    },
    [isResizing, onResize, isReordering, onReorder]
  );

  useEffect(
    function onWindowMouseChange() {
      window.addEventListener("mousemove", onWindowMouseMove);
      window.addEventListener("mouseup", onWindowMouseUp);
      return () => {
        window.removeEventListener("mousemove", onWindowMouseMove);
        window.removeEventListener("mouseup", onWindowMouseUp);
      };
    },
    [onWindowMouseMove, onWindowMouseUp]
  );

  useEffect(
    function onFixedColumnChange() {
      if (fixedColumns.length > 0 && rootContainer) {
        rootContainer.style.setProperty(
          "--right-position",
          `${-(fixedColumns.length + 0.8)}px`
        );
      }
    },
    [fixedColumns, rootContainer]
  );

  return (
    <div
      className={`${styles.header} header`}
      style={{
        gridTemplateColumns: tableGridTemplate,
        width: `${totalWidth}px`
      }}
      onMouseDown={onHeaderMouseDown}
    >
      <div
        className={`${styles.leftFixed}`}
        style={{
          gridTemplateColumns: fixedColumnGridTemplate,
          width: `${fixedColumnWidth}px`
        }}
        ref={leftFixedRef}
      >
        <RowHeader
          isRowSelect={false}
          canSelect={canSelectAll}
          onChange={onSelectAll}
          checked={allSelected}
        />
        {previewOption && (
          <ColumnHeader column={{ id: "preview" }} canResize={false} />
        )}

        {fixedColumns.map((column, i) => {
          return (
            <ColumnHeader
              column={column}
              key={column.id}
              pinColumn={() => handlePinColumn(column, false)}
            />
          );
        })}
      </div>
      {printMode &&
        fixedColumns.map((column, i) => {
          return (
            <ColumnHeader
              column={column}
              key={column.id}
              pinColumn={() => handlePinColumn(column, false)}
            />
          );
        })}
      {nonFixedColumns.map((column, i) => {
        return (
          <ColumnHeader
            column={column}
            key={column.id}
            pinColumn={() => handlePinColumn(column, true)}
          />
        );
      })}
      {canAddColumn && (
        <div className={styles.rightFixed}>
          {addColumnPopup ? (
            <BaseDropdown
              targetRenderer={() => {
                return <button className={`${styles.addButton} shadow-12dp`} />;
              }}
              listRenderer={({ closePopup }) => {
                return <div>{addColumnPopup({ closePopup })}</div>;
              }}
            />
          ) : (
            <button
              className={`${styles.addButton} shadow-12dp`}
              onClick={onAddColumn}
            />
          )}
        </div>
      )}
      {/* <div className={styles.emptyspace} /> */}
      {isResizing && (
        <div
          className={styles.resizerBar}
          style={{ insetInlineStart: `${resizingLeft}px` }}
        />
      )}
      {isReordering && (
        <React.Fragment>
          <div
            className={styles.reorderOverlay}
            style={{
              insetInlineStart: `${reorderingLeft}px`,
              width: `${reorderingColumn.current.width}px`
            }}
          />
          <div
            className={styles.reorderHighlight}
            style={{ insetInlineStart: `${reorderingHighlightLeft.current}px` }}
          />
        </React.Fragment>
      )}
    </div>
  );
}

DataSheetHeaderComponent.propTypes = {
  totalWidth: PropTypes.number,
  tableGridTemplate: PropTypes.string,
  fixedColumnWidth: PropTypes.number,
  fixedColumnGridTemplate: PropTypes.string,
  fixedColumns: PropTypes.array,
  nonFixedColumns: PropTypes.array,
  onAddColumn: PropTypes.func,
  canAddColumn: PropTypes.bool,
  canSelectAll: PropTypes.bool,
  onSelectAll: PropTypes.func,
  onResize: PropTypes.func,
  onReorder: PropTypes.func,
  previewOption: PropTypes.bool,
  allSelected: PropTypes.bool,
  addColumnPopup: PropTypes.any,
  handlePinColumn: PropTypes.func,
  rootContainer: PropTypes.node
};

export const DataSheetHeader = memo(DataSheetHeaderComponent);
